import axios from 'axios';
import { ApiError, TRANSPORT_ERROR_CODES } from './api-error.js';
/** Routes that must never carry a bearer token and must never trigger a refresh. */
const PUBLIC_PATHS = [
    '/auth/otp/request',
    '/auth/otp/verify',
    '/auth/two-factor/challenge',
    '/auth/refresh',
];
const REFRESH_PATH = '/auth/refresh';
function isPublicPath(url) {
    const path = url ?? '';
    return PUBLIC_PATHS.some((p) => path.startsWith(p));
}
/**
 * The path part of a base URL, so `https://bookora.net/api/v1` and `/api/v1`
 * both reduce to `/api/v1`.
 */
function pathOf(root) {
    const match = /^[a-z][a-z0-9+.-]*:\/\/[^/]+(\/.*)?$/i.exec(root);
    const path = match ? (match[1] ?? '') : root;
    return path.replace(/\/+$/, '');
}
/**
 * Strips the client's own root from a URL that already carries it.
 *
 * Swagger publishes the *deployed* paths — `/api/v1/auth/me` — so that is what
 * a generated client (orval, in `clinic-panel/src/api/generated/`) bakes into
 * every call. This instance is already rooted there, and axios would join the
 * two into `/api/v1/api/v1/auth/me`. Worse than the 404: `isPublicPath` would
 * stop matching, so a wrong OTP would try to refresh a session the user does
 * not have yet. No real endpoint is ever addressed by its full deployed path
 * relative to the root, so this can only ever undo the duplication.
 */
export function stripRootPrefix(url, rootPath) {
    if (url === undefined || rootPath === '')
        return url;
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url))
        return url;
    if (url === rootPath)
        return '/';
    return url.startsWith(`${rootPath}/`) ? url.slice(rootPath.length) : url;
}
function toApiError(err) {
    const status = err.response?.status;
    const body = err.response?.data;
    if (axios.isCancel(err)) {
        return new ApiError({
            statusCode: 0,
            code: TRANSPORT_ERROR_CODES.CANCELLED,
        });
    }
    if (body && typeof body === 'object' && typeof body.code === 'string') {
        return new ApiError({
            statusCode: body.statusCode ?? status ?? 0,
            code: body.code,
            message: body.message,
            details: body.details,
            requestId: body.requestId,
        });
    }
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
        return new ApiError({
            statusCode: 0,
            code: TRANSPORT_ERROR_CODES.TIMEOUT,
            message: err.message,
        });
    }
    if (!err.response) {
        return new ApiError({
            statusCode: 0,
            code: TRANSPORT_ERROR_CODES.NETWORK,
            message: err.message,
        });
    }
    return new ApiError({
        statusCode: status ?? 0,
        code: TRANSPORT_ERROR_CODES.MALFORMED,
        message: err.message,
    });
}
function unwrap(res) {
    const body = res.data;
    if (body && typeof body === 'object' && 'data' in body) {
        return body.data;
    }
    return body;
}
export function createApiClient(options) {
    const { baseUrl, tokens, onSessionExpired, headers, timeoutMs = 20_000 } = options;
    const root = `${baseUrl.replace(/\/+$/, '')}/v1`;
    const rootPath = pathOf(root);
    const instance = axios.create({ baseURL: root, timeout: timeoutMs });
    instance.interceptors.request.use((config) => {
        // Before axios joins `baseURL` and `url`, so a generated client's absolute
        // `/api/v1/...` route is not requested twice-prefixed.
        config.url = stripRootPrefix(config.url, rootPath);
        if (!isPublicPath(config.url)) {
            const { accessToken } = tokens.getSnapshot();
            if (accessToken)
                config.headers.set('Authorization', `Bearer ${accessToken}`);
        }
        for (const [key, value] of Object.entries(headers?.() ?? {})) {
            config.headers.set(key, value);
        }
        return config;
    });
    let refreshInFlight = null;
    /**
     * One refresh, retried on TRANSIENT failures only.
     *
     * This used to answer every failure with "session over" — a timeout, a
     * dropped connection or a 502 logged the operator out exactly like a revoked
     * session did. On the censored link a refresh fails in transit routinely, so
     * the panels logged people out roughly every access-token cycle. Now only
     * the server saying no (a 4xx from /auth/refresh) ends the session; anything
     * else keeps the tokens and reports `transient`, and the request that
     * triggered it fails as a network error the user can retry. If the server
     * DID rotate before the response was lost, the retry presents the old token
     * inside the backend's benign-replay window and is reissued, not punished.
     */
    async function performRefresh() {
        const { refreshToken } = tokens.getSnapshot();
        if (!refreshToken)
            return { kind: 'rejected' };
        const delays = [0, 1_000, 3_000];
        for (const delay of delays) {
            if (delay)
                await new Promise((resolve) => setTimeout(resolve, delay));
            try {
                // A bare axios call, not `instance`: going through the instance would
                // re-enter this very interceptor if the refresh itself 401s.
                const res = await axios.post(`${root}${REFRESH_PATH}`, { refreshToken }, { timeout: 15_000 });
                const pair = res.data?.data;
                if (pair?.accessToken && pair.refreshToken) {
                    tokens.set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
                    return { kind: 'ok', accessToken: pair.accessToken };
                }
                return { kind: 'rejected' };
            }
            catch (error) {
                const status = axios.isAxiosError(error) ? error.response?.status : undefined;
                // 4xx: the server looked at the token and refused it. Final.
                if (status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429) {
                    return { kind: 'rejected' };
                }
                // No response, a timeout, 408/429 or a 5xx: try again.
            }
        }
        return { kind: 'transient' };
    }
    function refreshAccessToken() {
        refreshInFlight ??= performRefresh().finally(() => {
            refreshInFlight = null;
        });
        return refreshInFlight;
    }
    /** Drops the local session and tells the app shell why, exactly once. */
    function endSession(err) {
        if (tokens.getSnapshot().accessToken === null && tokens.getSnapshot().refreshToken === null) {
            return;
        }
        tokens.clear();
        onSessionExpired?.(err.response?.data?.code ?? 'AUTH_INVALID');
    }
    instance.interceptors.response.use((res) => res, async (err) => {
        const original = err.config;
        const status = err.response?.status;
        const guest = isPublicPath(original?.url);
        if (status === 401 && original && !original._retried && !guest) {
            original._retried = true;
            const outcome = await refreshAccessToken();
            if (outcome.kind === 'ok') {
                original.headers.set('Authorization', `Bearer ${outcome.accessToken}`);
                return instance.request(original);
            }
            // Transient: keep the session; this request fails, the next one retries.
            if (outcome.kind === 'rejected')
                endSession(err);
        }
        else if (status === 401 && !guest) {
            // A 401 a refresh cannot fix: the retry already 401'd, or there was no
            // refresh token to begin with. Without this branch the session sits
            // there looking valid and every subsequent request fails silently.
            endSession(err);
        }
        throw toApiError(err);
    });
    async function request(config) {
        const res = await instance.request(config);
        return unwrap(res);
    }
    async function requestPaged(config) {
        const res = await instance.request(config);
        const payload = unwrap(res);
        if (Array.isArray(payload)) {
            return { items: payload, page: 1, pageSize: payload.length, total: payload.length, pageCount: 1 };
        }
        return payload;
    }
    return {
        http: instance,
        request,
        requestPaged,
        get: (url, params, config) => request({ ...config, method: 'GET', url, params }),
        getPaged: (url, params, config) => requestPaged({ ...config, method: 'GET', url, params }),
        post: (url, body, config) => request({ ...config, method: 'POST', url, data: body }),
        patch: (url, body, config) => request({ ...config, method: 'PATCH', url, data: body }),
        put: (url, body, config) => request({ ...config, method: 'PUT', url, data: body }),
        del: (url, config) => request({ ...config, method: 'DELETE', url }),
    };
}
//# sourceMappingURL=client.js.map